/**
 * Probe calibration — expected vs actual. Express works out pass/fail
 * (|actual − expected| ≤ tolerance); these mirror it for the live preview.
 */

import {
  FOOD_SAFETY_CALIBRATION_DEFAULTS,
  FOOD_SAFETY_CALIBRATION_DEFAULT_TEST,
  FOOD_SAFETY_CALIBRATION_TEST,
  FOOD_SAFETY_CALIBRATION_TOLERANCE_BOUNDS,
  FOOD_SAFETY_DISHWASHER_DEFAULTS,
  FOOD_SAFETY_EQUIPMENT_KIND,
} from 'src/constants/foodSafety'
import {
  buildFoodSafetyCorrectiveActionBody,
  formatFoodSafetyTemp,
  isFoodSafetyRecheckLog,
  pickFoodSafetyLogException,
} from 'src/utils/foodSafetyCorrectiveAction'
import { foodSafetyTempToneFromOutOfRange } from 'src/utils/foodSafetyTempColour'
import { formatFoodSafetyClockTime } from 'src/utils/foodSafetyTime'

const round2 = n => Math.round(n * 100) / 100

const numberOrNull = value =>
  value == null || value === '' || Number.isNaN(Number(value))
    ? null
    : Number(value)

/** Digits still being typed, including a trailing dot or a leading minus. */
const PROBE_READING_IN_PROGRESS = /^-?\d*\.?\d*$/

/**
 * Value for a probe temperature field. A finished number stays. A half-typed
 * number stays so the decimal can be finished. Anything else, including the
 * word "undefined", is an empty field.
 */
export function probeInputNumberValue(value) {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value !== 'string') return null
  if (value === '' || value === 'undefined') return null
  if (PROBE_READING_IN_PROGRESS.test(value)) return value
  return numberOrNull(value)
}

export const FOOD_SAFETY_PROBE_TEST_OPTIONS = Object.freeze(
  Object.values(FOOD_SAFETY_CALIBRATION_TEST).map(test => ({
    test,
    label: FOOD_SAFETY_CALIBRATION_DEFAULTS[test].label,
  })),
)

const BOTH = FOOD_SAFETY_CALIBRATION_TEST.ICE_AND_BOILING
const WASH = 'wash'
const RINSE = 'rinse'

/** True when one check records ice water and boiling water together. */
export const isProbeBothTests = config => config?.calibrationTest === BOTH

/** A dishwasher in this section. Missing equipmentKind means a probe. */
export const isDishwasherUnit = config =>
  config?.equipmentKind === FOOD_SAFETY_EQUIPMENT_KIND.DISHWASHER

/** Probe or dishwasher defaults when Setup switches the unit type. */
export function equipmentKindFormPatch(kind) {
  if (kind === FOOD_SAFETY_EQUIPMENT_KIND.DISHWASHER) {
    return {
      equipmentKind: FOOD_SAFETY_EQUIPMENT_KIND.DISHWASHER,
      referenceTemp: FOOD_SAFETY_DISHWASHER_DEFAULTS.wash.target,
      boilingReferenceTemp: FOOD_SAFETY_DISHWASHER_DEFAULTS.rinse.target,
    }
  }
  return {
    equipmentKind: FOOD_SAFETY_EQUIPMENT_KIND.PROBE,
    ...probeTestPreset(FOOD_SAFETY_CALIBRATION_DEFAULT_TEST),
  }
}

/** Reference temp and tolerance for a test preset (ice 0 ±1, boiling 100 ±1). */
export function probeTestPreset(test) {
  const preset =
    FOOD_SAFETY_CALIBRATION_DEFAULTS[test] ||
    FOOD_SAFETY_CALIBRATION_DEFAULTS[FOOD_SAFETY_CALIBRATION_DEFAULT_TEST]
  const result = {
    calibrationTest: FOOD_SAFETY_CALIBRATION_DEFAULTS[test]
      ? test
      : FOOD_SAFETY_CALIBRATION_DEFAULT_TEST,
    referenceTemp: preset.target,
    tolerance: round2(preset.tempMax - preset.target),
  }
  return test === BOTH
    ? { ...result, boilingReferenceTemp: preset.boilingTarget }
    : result
}

const band = (calibrationTest, referenceTemp, tolerance) => ({
  calibrationTest,
  label: FOOD_SAFETY_CALIBRATION_DEFAULTS[calibrationTest].label,
  referenceTemp,
  tolerance,
  tempMin: round2(referenceTemp - tolerance),
  tempMax: round2(referenceTemp + tolerance),
})

/**
 * Mirrors Express resolveFoodSafetyProbeConfig — blanks come from the preset.
 * Both tests: `referenceTemp` is ice, `boilingReferenceTemp` boiling, and
 * `tests` has each pass band.
 */
export function resolveProbeConfig(config = {}) {
  const base = config && typeof config === 'object' ? config : {}
  if (isDishwasherUnit(base)) {
    const wash =
      numberOrNull(base.referenceTemp) ??
      FOOD_SAFETY_DISHWASHER_DEFAULTS.wash.target
    const rinse =
      numberOrNull(base.boilingReferenceTemp) ??
      FOOD_SAFETY_DISHWASHER_DEFAULTS.rinse.target
    return {
      equipmentKind: FOOD_SAFETY_EQUIPMENT_KIND.DISHWASHER,
      passRule: 'minimum',
      referenceTemp: wash,
      boilingReferenceTemp: rinse,
      tests: [
        {
          calibrationTest: WASH,
          label: FOOD_SAFETY_DISHWASHER_DEFAULTS.wash.label,
          referenceTemp: wash,
          passRule: 'minimum',
          tempMin: wash,
          tempMax: null,
        },
        {
          calibrationTest: RINSE,
          label: FOOD_SAFETY_DISHWASHER_DEFAULTS.rinse.label,
          referenceTemp: rinse,
          passRule: 'minimum',
          tempMin: rinse,
          tempMax: null,
        },
      ],
    }
  }
  const preset = probeTestPreset(base.calibrationTest)
  const referenceTemp = numberOrNull(base.referenceTemp) ?? preset.referenceTemp
  const tolerance = numberOrNull(base.tolerance) ?? preset.tolerance
  const resolved = {
    probeReference: String(base.probeReference || '').trim() || null,
    calibrationTest: preset.calibrationTest,
    referenceTemp,
    tolerance,
    tempMin: round2(referenceTemp - tolerance),
    tempMax: round2(referenceTemp + tolerance),
  }
  if (preset.calibrationTest !== BOTH) return resolved
  const boilingReferenceTemp =
    numberOrNull(base.boilingReferenceTemp) ?? preset.boilingReferenceTemp
  return {
    ...resolved,
    boilingReferenceTemp,
    tests: [
      band(FOOD_SAFETY_CALIBRATION_TEST.ICE_WATER, referenceTemp, tolerance),
      band(
        FOOD_SAFETY_CALIBRATION_TEST.BOILING_WATER,
        boilingReferenceTemp,
        tolerance,
      ),
    ],
  }
}

export function formatProbeTestLabel(config) {
  if (isDishwasherUnit(config)) return 'Dishwasher'
  const { calibrationTest } = resolveProbeConfig(config)
  return FOOD_SAFETY_CALIBRATION_DEFAULTS[calibrationTest].label
}

/** "expected 0°C ±1°C" · both: "expected 0°C and 100°C ±1°C" */
export function formatProbeExpected(config) {
  if (isDishwasherUnit(config)) {
    const { referenceTemp, boilingReferenceTemp } = resolveProbeConfig(config)
    return `wash at or above ${formatFoodSafetyTemp(
      referenceTemp,
    )}, rinse at or above ${formatFoodSafetyTemp(boilingReferenceTemp)}`
  }
  const { referenceTemp, boilingReferenceTemp, tolerance, tests } =
    resolveProbeConfig(config)
  const targets = tests
    ? `${formatFoodSafetyTemp(referenceTemp)} and ${formatFoodSafetyTemp(
        boilingReferenceTemp,
      )}`
    : formatFoodSafetyTemp(referenceTemp)
  return `expected ${targets} ±${formatFoodSafetyTemp(tolerance)}`
}

/** One test's band from a both-tests config (ice or boiling). */
export function resolveProbeTestConfig(config, calibrationTest) {
  const resolved = resolveProbeConfig(config)
  const test = resolved.tests?.find(t => t.calibrationTest === calibrationTest)
  return test ? { ...resolved, ...test, tests: undefined } : resolved
}

/** "Ice water · expected 0°C ±1°C" */
export function formatProbeTestSummary(config) {
  return `${formatProbeTestLabel(config)} · ${formatProbeExpected(config)}`
}

/** True / false once a reading is typed; null while blank. */
export function isProbeReadingPassing(value, config) {
  const actual = numberOrNull(value)
  if (actual == null) return null
  if (config?.passRule === 'minimum') {
    const target = numberOrNull(config.referenceTemp)
    if (target == null) return null
    return actual + 1e-9 >= target
  }
  const { referenceTemp, tolerance } = resolveProbeConfig(config)
  return Math.abs(actual - referenceTemp) <= tolerance + 1e-9
}

/**
 * Both tests: `{ ice, boiling, passed, failedTests }` — `passed` is null until
 * both readings are typed; `failedTests` lists the bands that failed.
 */
export function evaluateProbeReadings({ value, boilingValue }, config) {
  const resolved = resolveProbeConfig(config)
  if (!resolved.tests) {
    const passed = isProbeReadingPassing(value, config)
    return {
      ice: passed,
      boiling: null,
      passed,
      failedTests: passed === false ? [resolved] : [],
    }
  }
  const [iceBand, boilingBand] = resolved.tests
  const ice = isProbeReadingPassing(value, iceBand)
  const boiling = isProbeReadingPassing(boilingValue, boilingBand)
  const failedTests = [
    ice === false ? { ...iceBand, value } : null,
    boiling === false ? { ...boilingBand, value: boilingValue } : null,
  ].filter(Boolean)
  return {
    ice,
    boiling,
    passed: failedTests.length ? false : ice && boiling ? true : null,
    failedTests,
  }
}

/**
 * True when the probe's latest test passed, or failed and then passed its
 * retest — the card then offers "Log another test" instead of an open
 * "Check again" field.
 */
export function isProbeLatestTestPassing(logs) {
  const latest = (Array.isArray(logs) ? logs : [])
    .filter(log => !isFoodSafetyRecheckLog(log) && log?.completedAt)
    .sort(
      (a, b) =>
        new Date(b.completedAt).getTime() - new Date(a.completedAt).getTime(),
    )[0]
  if (!latest) return false
  if (latest.payload?.calibrationPassed === true) return true
  const exceptions = latest.exceptions?.length
    ? latest.exceptions
    : [pickFoodSafetyLogException(latest)]
  return exceptions.every(exception => {
    const recheck = exception?.recheck
    if (!recheck || recheck.value == null) return false
    if (typeof recheck.passed === 'boolean') return recheck.passed
    return recheck.outOfRange !== true
  })
}

/**
 * Panel headline: "⚠ Probe reads 2.4°C — expected 0°C ±1°C". Both tests:
 * "⚠ Boiling water: probe reads 97°C — expected 100°C ±1°C".
 */
export function formatProbeFailHeadline(value, config) {
  const temp = formatFoodSafetyTemp(value)
  if (config?.passRule === 'minimum') {
    const name = config.label || 'Reading'
    return `⚠ ${name} reads ${temp || '—'} — at or above ${formatFoodSafetyTemp(
      config.referenceTemp,
    )}`
  }
  if (config?.label && config?.calibrationTest !== BOTH) {
    return `⚠ ${config.label}: probe reads ${
      temp || '—'
    } — ${formatProbeExpected(config)}`
  }
  return `⚠ Probe reads ${temp || '—'} — ${formatProbeExpected(config)}`
}

/** Both tests failed: one headline naming each. */
export function formatProbeFailedTestsHeadline(failedTests) {
  if (failedTests.length < 2) {
    const [test] = failedTests
    return test ? formatProbeFailHeadline(test.value, test) : ''
  }
  return `⚠ Both tests failed — ${failedTests
    .map(t => `${t.label.toLowerCase()} ${formatFoodSafetyTemp(t.value)}`)
    .join(', ')}`
}

/** Panel reading — the pass band, so a recheck outside it is flagged too. */
export function probePanelReading(value, config) {
  if (config?.passRule === 'minimum') {
    return {
      value,
      tempMin: config.tempMin ?? config.referenceTemp,
      tempMax: config.tempMax ?? null,
    }
  }
  const { tempMin, tempMax } = resolveProbeConfig(config)
  return { value, tempMin, tempMax }
}

const ICE = FOOD_SAFETY_CALIBRATION_TEST.ICE_WATER
const BOILING = FOOD_SAFETY_CALIBRATION_TEST.BOILING_WATER
const PROBE_TEST_ORDER = [ICE, BOILING, WASH, RINSE]
const SECOND_READING = new Set([BOILING, RINSE])

/**
 * One follow-up row per problem on a saved probe check. A two-test check with
 * both tests failed gets an "Ice water" row and a "Boiling water" row, each
 * with its own status, corrective action and retest; otherwise one unlabelled
 * row keyed by the log (as before).
 */
export function listProbeLogFollowUps(log, config) {
  const logId = String(log?._id || '')
  const snapshot = log?.configSnapshot || config
  const readingFor = test =>
    probePanelReading(
      SECOND_READING.has(test)
        ? log?.payload?.boilingValue
        : log?.payload?.value,
      resolveProbeTestConfig(snapshot, test),
    )
  const perTest = (Array.isArray(log?.exceptions) ? log.exceptions : [])
    .filter(
      e => e?._id && PROBE_TEST_ORDER.includes(e.context?.calibrationTest),
    )
    .sort(
      (a, b) =>
        PROBE_TEST_ORDER.indexOf(a.context.calibrationTest) -
        PROBE_TEST_ORDER.indexOf(b.context.calibrationTest),
    )
  if (perTest.length < 2) {
    const exception = pickFoodSafetyLogException(log)
    return [
      {
        key: logId,
        label: '',
        exception,
        fallbackReading: readingFor(
          exception?.context?.calibrationTest === BOILING ? BOILING : ICE,
        ),
      },
    ]
  }
  return perTest.map(exception => {
    const test = exception.context.calibrationTest
    return {
      key: `${logId}-${test}`,
      label: FOOD_SAFETY_CALIBRATION_DEFAULTS[test].label,
      exception,
      fallbackReading: readingFor(test),
    }
  })
}

export function buildProbeLogBody({
  definitionId,
  localDate,
  value,
  boilingValue,
  action,
}) {
  const payload = { value: Number(value) }
  if (boilingValue != null && boilingValue !== '') {
    payload.boilingValue = Number(boilingValue)
  }
  const body = { definitionId: String(definitionId), localDate, payload }
  const correctiveAction = buildFoodSafetyCorrectiveActionBody(action || {})
  return correctiveAction ? { ...body, correctiveAction } : body
}

/**
 * Saved check: "0.4°C · Pass" or "2.4°C · Fail" with what was expected that
 * day (from the log's snapshot, never today's probe settings). Pass the
 * probe's current config to drop the expected part when the card's meta line
 * already says the same thing.
 */
export function formatProbeLogLine(log, currentConfig = null) {
  const line = formatProbeLogLineWithExpected(log)
  if (
    currentConfig &&
    line.expectedSummary &&
    line.expectedSummary === formatProbeTestSummary(currentConfig)
  ) {
    return { ...line, expectedLabel: null }
  }
  return line
}

function formatProbeLogLineWithExpected(log) {
  const value = numberOrNull(log?.payload?.value)
  const snapshot = log?.configSnapshot || {}
  const boilingValue = numberOrNull(log?.payload?.boilingValue)
  if (boilingValue != null && isDishwasherUnit(snapshot)) {
    return formatDishwasherLogLine(log, value, boilingValue)
  }
  if (boilingValue != null) return formatProbeBothLogLine(log, value)
  const expected =
    numberOrNull(log?.payload?.expected) ?? numberOrNull(snapshot.referenceTemp)
  const config = { ...snapshot, referenceTemp: expected }
  const passed =
    typeof log?.payload?.calibrationPassed === 'boolean'
      ? log.payload.calibrationPassed
      : isProbeReadingPassing(value, config)
  const time = log?.completedAt
    ? formatFoodSafetyClockTime(log.completedAt)
    : ''
  return {
    tempLabel: formatFoodSafetyTemp(value) || null,
    statusLabel: passed == null ? null : passed ? 'Pass' : 'Fail',
    passed,
    tone: foodSafetyTempToneFromOutOfRange(passed == null ? null : !passed),
    expectedLabel:
      expected == null
        ? null
        : `${formatProbeTestLabel(config)}, ${formatProbeExpected(config)}`,
    expectedSummary: expected == null ? null : formatProbeTestSummary(config),
    meta: [log?.completedByName, time].filter(Boolean).join(' · '),
  }
}

/** "62°C / 84°C · Pass — wash at or above 55°C, rinse at or above 82°C" */
function formatDishwasherLogLine(log, value, rinseValue) {
  const payload = log?.payload || {}
  const snapshot = log?.configSnapshot || {}
  const config = {
    ...snapshot,
    equipmentKind: FOOD_SAFETY_EQUIPMENT_KIND.DISHWASHER,
    referenceTemp: numberOrNull(payload.expected) ?? snapshot.referenceTemp,
    boilingReferenceTemp:
      numberOrNull(payload.boilingExpected) ?? snapshot.boilingReferenceTemp,
  }
  const passed =
    typeof payload.calibrationPassed === 'boolean'
      ? payload.calibrationPassed
      : evaluateProbeReadings({ value, boilingValue: rinseValue }, config)
          .passed
  const time = log?.completedAt
    ? formatFoodSafetyClockTime(log.completedAt)
    : ''
  return {
    tempLabel: `${formatFoodSafetyTemp(value)} / ${formatFoodSafetyTemp(
      rinseValue,
    )}`,
    statusLabel: passed == null ? null : passed ? 'Pass' : 'Fail',
    passed,
    tone: foodSafetyTempToneFromOutOfRange(passed == null ? null : !passed),
    expectedLabel: formatProbeExpected(config),
    expectedSummary: formatProbeTestSummary(config),
    meta: [log?.completedByName, time].filter(Boolean).join(' · '),
  }
}

/** "0.2°C / 99.6°C · Pass — Ice water 0°C, boiling water 100°C ±1°C" */
function formatProbeBothLogLine(log, value) {
  const payload = log?.payload || {}
  const snapshot = log?.configSnapshot || {}
  const config = {
    ...snapshot,
    calibrationTest: BOTH,
    referenceTemp: numberOrNull(payload.expected) ?? snapshot.referenceTemp,
    boilingReferenceTemp:
      numberOrNull(payload.boilingExpected) ?? snapshot.boilingReferenceTemp,
  }
  const resolved = resolveProbeConfig(config)
  const passed =
    typeof payload.calibrationPassed === 'boolean'
      ? payload.calibrationPassed
      : evaluateProbeReadings(
          { value, boilingValue: payload.boilingValue },
          config,
        ).passed
  const time = log?.completedAt
    ? formatFoodSafetyClockTime(log.completedAt)
    : ''
  return {
    tempLabel: `${formatFoodSafetyTemp(value)} / ${formatFoodSafetyTemp(
      numberOrNull(payload.boilingValue),
    )}`,
    statusLabel: passed == null ? null : passed ? 'Pass' : 'Fail',
    passed,
    tone: foodSafetyTempToneFromOutOfRange(passed == null ? null : !passed),
    expectedLabel: `Ice water ${formatFoodSafetyTemp(
      resolved.referenceTemp,
    )}, boiling water ${formatFoodSafetyTemp(
      resolved.boilingReferenceTemp,
    )} ±${formatFoodSafetyTemp(resolved.tolerance)}`,
    expectedSummary: formatProbeTestSummary(config),
    meta: [log?.completedByName, time].filter(Boolean).join(' · '),
  }
}

/** Setup validation for the probe fields. Error string or null. */
export function validateProbeSetupFields(form) {
  if (isDishwasherUnit(form)) {
    const wash = numberOrNull(form?.referenceTemp)
    const rinse = numberOrNull(form?.boilingReferenceTemp)
    if (wash == null) return 'Enter the wash temperature it must reach.'
    if (wash < -50 || wash > 150) {
      return 'Wash temperature must be between -50 and 150°C.'
    }
    if (rinse == null) return 'Enter the rinse temperature it must reach.'
    if (rinse < -50 || rinse > 150) {
      return 'Rinse temperature must be between -50 and 150°C.'
    }
    return null
  }
  const ref = numberOrNull(form?.referenceTemp)
  if (ref == null) return 'Enter the temperature the probe should read.'
  if (ref < -50 || ref > 150) {
    return 'Reference temperature must be between -50 and 150°C.'
  }
  if (isProbeBothTests(form)) {
    const boiling = numberOrNull(form?.boilingReferenceTemp)
    if (boiling == null) {
      return 'Enter the temperature the probe should read in boiling water.'
    }
    if (boiling < -50 || boiling > 150) {
      return 'Boiling water temperature must be between -50 and 150°C.'
    }
  }
  const tol = numberOrNull(form?.tolerance)
  const { min, max } = FOOD_SAFETY_CALIBRATION_TOLERANCE_BOUNDS
  if (tol == null || tol < min || tol > max) {
    return `Allowed difference must be between ±${min} and ±${max}°C.`
  }
  return null
}
